import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import useSetBillingAddress from "../../hooks/useSetBillingAddress";
import {useCartContext} from "../../context/CartContext";
import {useUserContext} from "../../context/UserContext";
import {T} from "utils/I18n/I18n";
import CheckoutAddressesList from "./CheckoutAddressItem/CheckoutAddressesList";
import NewBillingAddressForm from "./CheckoutAddressItem/NewBillingAddressForm";
import FormInput from "components/UI/FormInput/FormInput";
import CountryById from "components/PersonalArea/PersonalAreaAddress/CountrySelect/CountryById";
import PostcodeAndCity from "./CheckoutAddressItem/SelectCityByPostcode/PostcodeAndCity";
import {countryCode} from "utils/checkout";
import CountryOptions from "./CountryOptions";
import Loader from "components/Loader";
import Vector from "assets/icons/Vector.png";
import Pencil from "assets/icons/pen 1.png";
import isVATValid from '../../utils/isVATValid';
const TYPE_PRIVATE = 1;
const TYPE_BUSINESS = 2;

const BillingFormFields = ({ billingType, setBillingType }) => {
    const [selectedAddress, setSelectedAddress] = useState('');
    const [editGuestAddress, setEditGuestAddress] = useState(false);
    const [showAddNewAddress, setShowAddNewAddress] = useState(false);
    const [sameAsShippingDisabled, setSameAsShippingDisabled] = useState(false);

    // const { register, handleSubmit, errors, setValue, watch } = useForm();

    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();



    const { setBillingAddress, loading: billingLoading } = useSetBillingAddress();
    const { cart } = useCartContext();
    const { user } = useUserContext();

    const shippingAddress = cart && cart.shipping_addresses[0];
    const billingAddress = cart && cart.billing_address;

    const isStorePickup =
        shippingAddress &&
        shippingAddress.selected_shipping_method &&
        shippingAddress.selected_shipping_method.carrier_code === 'storepickup';
    const isParcelShop =
        shippingAddress &&
        shippingAddress.selected_shipping_method &&
        shippingAddress.selected_shipping_method.carrier_code === 'glsparcelshop';

    const [sameAsShippingAddress, setSameAsShippingAddress] = useState(!cart.billing_address && !isStorePickup && !isParcelShop);
    const [showAddressList, setShowAddressList] = useState(!cart.billing_address );

    const showGuestForm = (!user && !billingAddress) || editGuestAddress || (!billingAddress && sameAsShippingAddress);
    const showBillingAddress = billingAddress && !showGuestForm && !showAddressList && !showAddNewAddress;
    const showSameAsShippingCheckbox = !showBillingAddress && !isStorePickup && !isParcelShop;

    const onExitHandler = () => {
        setShowAddressList(false);
        setShowAddNewAddress(false);
        setEditGuestAddress(false);
        if (billingAddress && billingAddress.company) {
            setBillingType(TYPE_BUSINESS);
        } else {
            setBillingType(TYPE_PRIVATE);
        }
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        if (showGuestForm && !sameAsShippingAddress && billingAddress) {
            if (billingType === TYPE_BUSINESS) {
                setValue('company', billingAddress.company);
                setValue('vat_id', billingAddress.vat_id);
            }

            setValue('firstname', billingAddress.firstname);
            setValue('lastname', billingAddress.lastname);
            setValue('street', billingAddress.street[0]);
            setValue('postcode', billingAddress.postcode);
            setValue('city', billingAddress.city);
            setValue('country_code', billingAddress.country.code);
            setValue('telephone', billingAddress.telephone);
        }
    }, [showGuestForm, sameAsShippingAddress]);

    useEffect(() => {
        if (billingAddress) {
            if (billingType === TYPE_PRIVATE ? billingAddress.company : !billingAddress.company) {
                if (user) {
                    if (sameAsShippingAddress) {
                        setEditGuestAddress(true);
                    } else {
                        setShowAddressList(true);
                        // setShowAddNewAddress(selectedAddress === 'addAddress');
                    }
                } else {
                    setEditGuestAddress(true);
                }
            }
        }
    }, [billingType, billingAddress]);

    const onSubmitHandler = async formData => {
        if (sameAsShippingAddress) {
            const address = { ...cart.shipping_addresses[0] };

            address.country_code = address.country.code;

            if (billingType === TYPE_BUSINESS) {
                address.company = formData.company;
                address.vat_id = formData.vat_id;
            }

            delete address.country;
            delete address.region;
            delete address.selected_shipping_method;
            delete address.pickup_location_code;
            delete address.customer_address_id;
            delete address.available_shipping_methods;
            // eslint-disable-next-line no-underscore-dangle
            delete address.__typename;

            await setBillingAddress({ address });
        } else {
            await setBillingAddress({ address: formData });
        }

        setEditGuestAddress(false);
        setShowAddNewAddress(false);
        setShowAddressList(false);
    };

    const onSameAsShippingChange = async event => {
        const { checked } = event.target;

        setSameAsShippingDisabled(true);

        try {
            if (checked) {
                setEditGuestAddress(true);
                setShowAddNewAddress(false);
                setShowAddressList(false);
            } else {
                setShowAddressList(true);
                setShowAddNewAddress(selectedAddress === 'addAddress');
                if (user) {
                    setEditGuestAddress(false);
                }
            }

            setSameAsShippingAddress(checked);
        } finally {
            setSameAsShippingDisabled(false);
        }
    };

    return (
        <div>
            {showSameAsShippingCheckbox && (
                <div className="checkout__main__form__checkbox ml-0">
                    <input
                        disabled={sameAsShippingDisabled}
                        checked={sameAsShippingAddress}
                        type="checkbox"
                        id="same_as_shipping"
                        onChange={onSameAsShippingChange}
                    />

                    <label htmlFor="same_as_shipping">
                        <T id="cartSummary.sameAddress" />
                    </label>
                </div>
            )}

            {user && showAddressList && (
                <>
                    <CheckoutAddressesList
                        addresses={user.addresses}
                        billingAddress={billingAddress}
                        selectedAddress={selectedAddress}
                        isBusiness={billingType === TYPE_BUSINESS}
                        setShowAddNewAddress={setShowAddNewAddress}
                        setSelectedAddress={setSelectedAddress}
                        setShowAddressList={setShowAddressList}
                        setBillingType={setBillingType}
                    />
                </>
            )}

            {user && showAddNewAddress && (
                <NewBillingAddressForm
                    selectedAddress={selectedAddress}
                    billingAddress={billingAddress}
                    setShowAddNewAddress={setShowAddNewAddress}
                    setShowAddressList={setShowAddressList}
                    setSelectedAddress={setSelectedAddress}
                    setBillingType={setBillingType}
                    isBusiness={billingType === TYPE_BUSINESS}
                />
            )}

            {showGuestForm && (
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    {billingType === TYPE_BUSINESS && (
                        <>
                            <FormInput
                                text={<T id="cartSummary.companyName" />}
                                id="company"
                                name="company"
                                type="text"
                                register={register}
                                errors={errors}
                            />
                            <FormInput
                                errors={errors}
                                text={<T id="address.vatId" />}
                                id="vat_id"
                                type="text"
                                name="vat_id"
                                validationRules={{
                                    required: true,
                                    validate: { oib: isVATValid }
                                }}
                                register={register}
                            />
                        </>
                    )}

                    {sameAsShippingAddress && (
                        <div className="checkout__main__form__address mt-12">
                            <p>
                                {shippingAddress.firstname} {shippingAddress.lastname}
                            </p>
                            <p>{shippingAddress.street}</p>
                            <p>
                                {shippingAddress.city}, {shippingAddress.postcode}
                            </p>
                            <p>
                                <CountryById id={shippingAddress.country.code} />
                            </p>
                            <p>{shippingAddress.telephone}</p>
                        </div>
                    )}

                    {!sameAsShippingAddress && (
                        <>
                            <FormInput
                                register={register}
                                type="text"
                                text={<T id="registration.name" />}
                                name="firstname"
                                errors={errors}
                            />

                            <FormInput
                                register={register}
                                type="text"
                                text={<T id="registration.lastName" />}
                                name="lastname"
                                errors={errors}
                            />

                            <FormInput
                                register={register}
                                type="text"
                                text={<T id="address.street" />}
                                minLength={3}
                                name="street"
                                errors={errors}
                            />

                            <PostcodeAndCity register={register} errors={errors} watch={watch} setValue={setValue} />

                            <FormInput
                                register={register}
                                errors={errors}
                                text={<T id="address.country" />}
                                name="country_code"
                                onChange={() => {
                                    setValue('city', '');
                                    setValue('postcode', '');
                                }}
                            >
                                {countryCode === 'HR' && <option value="HR">Hrvatska</option>}
                                {countryCode === 'RS' && <option value="RS">Srbija</option>}
                                {countryCode === 'SI' && <option value="SI">Slovenija</option>}
                                <CountryOptions
                                    onFetch={() => billingAddress && setValue('country_code', billingAddress.country.code)}
                                />
                            </FormInput>

                            <FormInput
                                register={register}
                                type="number"
                                text={<T id="cartSummary.cellphoneNumber" />}
                                name="telephone"
                                errors={errors}
                            />
                        </>
                    )}

                    <div className="w-113 mt-12 float-right">
                        {billingLoading ? (
                            <div className="mt-12">
                                <Loader height="xl" />
                            </div>
                        ) : (
                            <div className="checkout-address-item__new-address__buttons">

                                {billingAddress && (
                                    <button className="checkout__main__form__button exit" onClick={onExitHandler} type="button">
                                        <T id="address.exit" />
                                    </button>
                                )}

                                <button className="checkout__main__form__button" type="submit">
                                    <T id="address.confirm" />
                                </button>
                            </div>
                        )}
                    </div>
                </form>
            )}

            {showBillingAddress && (
                <div className="checkout__main__form__address">
                    <p>{billingAddress.company}</p>
                    <p>{billingAddress.vat_id}</p>
                    <p>
                        {billingAddress.firstname} {billingAddress.lastname}
                    </p>
                    <p>{billingAddress.street}</p>
                    <p>
                        {billingAddress.city}, {billingAddress.postcode}
                    </p>
                    <p>
                        <CountryById id={billingAddress.country.code} />
                    </p>
                    <p>{billingAddress.telephone}</p>

                    <img className="checkout-address-item__is-active-vector" src={Vector} />

                    <div
                        className="checkout-address-item__send-here mt-12"
                        onClick={() => {
                            if (user) {
                                setShowAddressList(true);
                                setShowAddNewAddress(selectedAddress === 'addAddress');
                                setSameAsShippingAddress(false);

                                if (!selectedAddress) {
                                    const firstAddress = user.addresses[0];
                                    setSelectedAddress((firstAddress && firstAddress.id) || 'addAddress');
                                }
                            } else {
                                setEditGuestAddress(true);
                                setSameAsShippingAddress(false);
                            }
                        }}
                    >
                        <img src={Pencil} />
                        <span className="checkout-address-item__send-here">
              <T id="address.edit" />
            </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingFormFields